package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminApiClient
import twist.mena.sports.admin.api.AdminNotificationSettingsApi
import twist.mena.sports.admin.components.Column
import twist.mena.sports.admin.components.DataTable
import twist.mena.sports.admin.screens.notifications.HistoryTab
import twist.mena.sports.admin.screens.notifications.SettingsTab
import twist.mena.sports.admin.state.SHOW_REVAMP
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.*

private enum class NotificationTab(val label: String) {
    COMPOSE("Compose"),
    ACTIVITY("Activity"),
    AUDIENCE("Audience"),
    HISTORY("History"),
    SETTINGS("Settings")
}

// ─────────────────────────────────────────────────────────
//  Link auto-generation from entity type + ID
// ─────────────────────────────────────────────────────────

private fun generateLink(entityType: String, entityId: String, legacyId: String?): String {
    // Flutter route format: /home/sports/{type}/{id}
    return when (entityType) {
        "legacy_team" -> legacyId?.let { "/home/sports/team/$it" } ?: ""
        "legacy_league" -> legacyId?.let { "/home/sports/league/$it" } ?: ""
        "legacy_player" -> legacyId?.let { "/home/sports/player/$it" } ?: ""
        "fixture" -> "/home/sports/match/$entityId"
        "team" -> "/home/sports/team/$entityId"
        "league" -> "/home/sports/league/$entityId"
        "player" -> "/home/sports/player/$entityId"
        else -> ""
    }
}

private fun isLegacyType(entityType: String) =
    entityType.startsWith("legacy_")

// With the revamp hidden there is only one kind of team/league/player, so the "Legacy" qualifier is dropped.
private fun legacyLabel(name: String) = if (SHOW_REVAMP) "Legacy $name" else name

private fun entityTypeLabel(entityType: String): String = when (entityType) {
    "legacy_team" -> legacyLabel("Team")
    "legacy_league" -> legacyLabel("League")
    "legacy_player" -> legacyLabel("Player")
    "team" -> "Team"
    "league" -> "League"
    "player" -> "Player"
    "fixture" -> "Fixture"
    "broadcast" -> "Broadcast"
    else -> entityType
}

// ─────────────────────────────────────────────────────────
//  Main Screen
// ─────────────────────────────────────────────────────────

@Composable
fun NotificationsScreen(apiClient: AdminApiClient, settingsApi: AdminNotificationSettingsApi) {
    val colors = AppTheme.colors
    var activeTab by remember { mutableStateOf(NotificationTab.COMPOSE) }

    // Shared state: when user clicks "Duplicate" in Activity or "Send" in Topics
    var prefillRequest by remember { mutableStateOf<AdminNotificationHistoryResponse?>(null) }
    var prefillTopicType by remember { mutableStateOf<String?>(null) }
    var prefillTopicId by remember { mutableStateOf<String?>(null) }
    var prefillTopicName by remember { mutableStateOf<String?>(null) }

    Div({
        style {
            display(DisplayStyle.Flex)
            gap(Spacing.xs)
            marginBottom(Spacing.lg)
            property("border-bottom", "2px solid ${colors.border}")
            paddingBottom(Spacing.xs)
        }
    }) {
        NotificationTab.entries.forEach { tab ->
            Div({
                style {
                    padding(Spacing.sm, Spacing.md)
                    cursor("pointer")
                    borderRadius(Spacing.xs, Spacing.xs, 0.px, 0.px)
                    fontWeight(if (activeTab == tab) "600" else "400")
                    color(Color(if (activeTab == tab) colors.primary else colors.textMuted))
                    property("white-space", "nowrap")
                    if (activeTab == tab) {
                        property("border-bottom", "2px solid ${colors.primary}")
                        marginBottom((-2).px)
                    }
                }
                onClick { activeTab = tab }
            }) { Text(tab.label) }
        }
    }

    when (activeTab) {
        NotificationTab.COMPOSE -> ComposeTab(apiClient, prefillRequest, prefillTopicType, prefillTopicId, prefillTopicName) {
            prefillRequest = null; prefillTopicType = null; prefillTopicId = null; prefillTopicName = null
        }
        NotificationTab.ACTIVITY -> ActivityTab(apiClient) { hist ->
            prefillRequest = hist
            activeTab = NotificationTab.COMPOSE
        }
        NotificationTab.AUDIENCE -> AudienceTab(apiClient) { entityType, entityId, entityName ->
            prefillTopicType = entityType
            prefillTopicId = entityId
            prefillTopicName = entityName
            activeTab = NotificationTab.COMPOSE
        }
        NotificationTab.HISTORY -> HistoryTab(settingsApi)
        NotificationTab.SETTINGS -> SettingsTab(settingsApi)
    }
}

// ═══════════════════════════════════════════════════════════
//  COMPOSE TAB
// ═══════════════════════════════════════════════════════════

@Composable
private fun ComposeTab(
    apiClient: AdminApiClient,
    prefill: AdminNotificationHistoryResponse?,
    prefillTopicType: String?,
    prefillTopicId: String?,
    prefillTopicName: String?,
    onPrefillConsumed: () -> Unit
) {
    val colors = AppTheme.colors
    var titleAr by remember { mutableStateOf("") }
    var titleEn by remember { mutableStateOf("") }
    var bodyAr by remember { mutableStateOf("") }
    var bodyEn by remember { mutableStateOf("") }
    var targetType by remember { mutableStateOf("broadcast") }
    var targetEntityType by remember { mutableStateOf("legacy_team") }
    var targetEntityId by remember { mutableStateOf("") }
    var targetLegacyId by remember { mutableStateOf<String?>(null) }
    var link by remember { mutableStateOf("") }
    var linkAutoGenerated by remember { mutableStateOf(true) }
    var imageUrl by remember { mutableStateOf("") }
    var scheduleEnabled by remember { mutableStateOf(false) }
    var scheduledAt by remember { mutableStateOf("") }
    var sending by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var entitySearch by remember { mutableStateOf("") }
    var entityResults by remember { mutableStateOf<List<AdminEntitySearchResult>>(emptyList()) }
    var selectedEntityName by remember { mutableStateOf("") }
    var selectedEntityNameAr by remember { mutableStateOf("") }
    var showConfirmation by remember { mutableStateOf(false) }
    // Audience filters
    var filterPlatforms by remember { mutableStateOf<Set<String>>(emptySet()) }
    var filterLocale by remember { mutableStateOf<String?>(null) }
    var filterMinAge by remember { mutableStateOf("") }
    var filterMaxAge by remember { mutableStateOf("") }
    var audienceEstimate by remember { mutableStateOf<Int?>(null) }
    var estimating by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    // Apply prefill from "Duplicate" action
    LaunchedEffect(prefill) {
        if (prefill != null) {
            titleAr = prefill.titleAr
            titleEn = prefill.titleEn
            bodyAr = prefill.bodyAr ?: ""
            bodyEn = prefill.bodyEn ?: ""
            targetType = if (prefill.entityType == "broadcast") "broadcast" else "topic"
            if (prefill.entityType != "broadcast") {
                targetEntityType = prefill.entityType
                targetEntityId = prefill.entityId
            }
            link = prefill.link ?: ""
            linkAutoGenerated = false
            imageUrl = prefill.imageUrl ?: ""
            message = "Pre-filled from previous notification. Review and send."
            onPrefillConsumed()
        }
    }

    // Apply prefill from "Send" in Topics
    LaunchedEffect(prefillTopicType) {
        if (prefillTopicType != null && prefillTopicId != null) {
            targetType = "topic"
            targetEntityType = prefillTopicType
            targetEntityId = prefillTopicId
            selectedEntityName = prefillTopicName ?: ""
            entitySearch = prefillTopicName ?: ""
            message = "Target pre-filled from topic. Add content and send."
            onPrefillConsumed()
        }
    }

    // Search entities when type or query changes
    LaunchedEffect(targetType, targetEntityType, entitySearch) {
        if (targetType == "topic" && entitySearch.length >= 2) {
            try {
                entityResults = apiClient.searchEntities(entitySearch, targetEntityType)
            } catch (_: Exception) {
                entityResults = emptyList()
            }
        } else {
            entityResults = emptyList()
        }
    }

    // Estimate audience when target or filters change
    LaunchedEffect(targetType, targetEntityType, targetEntityId, filterPlatforms, filterLocale, filterMinAge, filterMaxAge) {
        if (titleAr.isNotBlank() || titleEn.isNotBlank() || targetType == "broadcast" || targetEntityId.isNotBlank()) {
            estimating = true
            try {
                val result = apiClient.estimateAudience(AdminSendNotificationRequest(
                    titleAr = "est", titleEn = "est", targetType = targetType,
                    targetEntityType = if (targetType == "topic") targetEntityType else null,
                    targetEntityId = if (targetType == "topic") targetEntityId.ifBlank { null } else null,
                    platforms = filterPlatforms.toList().ifEmpty { null },
                    locales = filterLocale?.let { listOf(it) },
                    minAge = filterMinAge.toIntOrNull(),
                    maxAge = filterMaxAge.toIntOrNull()
                ))
                audienceEstimate = result.estimatedCount
            } catch (_: Exception) {
                audienceEstimate = null
            } finally { estimating = false }
        }
    }

    // Auto-generate link when entity selection changes
    LaunchedEffect(targetEntityType, targetEntityId, targetLegacyId) {
        if (linkAutoGenerated && targetType == "topic" && targetEntityId.isNotBlank()) {
            link = generateLink(targetEntityType, targetEntityId, targetLegacyId)
        }
    }

    // ── Confirmation Dialog ──
    if (showConfirmation) {
        ConfirmationDialog(
            colors = colors,
            targetType = targetType,
            entityType = targetEntityType,
            entityName = selectedEntityName,
            entityNameAr = selectedEntityNameAr,
            legacyId = targetLegacyId,
            titleAr = titleAr,
            titleEn = titleEn,
            bodyAr = bodyAr,
            bodyEn = bodyEn,
            link = link,
            imageUrl = imageUrl,
            scheduleEnabled = scheduleEnabled,
            scheduledAt = scheduledAt,
            onConfirm = {
                showConfirmation = false
                sending = true
                message = null
                scope.launch {
                    try {
                        val scheduleIso = if (scheduleEnabled && scheduledAt.isNotBlank()) "${scheduledAt}:00Z" else null
                        val result = apiClient.sendNotification(AdminSendNotificationRequest(
                            titleAr = titleAr, titleEn = titleEn,
                            bodyAr = bodyAr.ifBlank { null }, bodyEn = bodyEn.ifBlank { null },
                            targetType = targetType,
                            targetEntityType = if (targetType == "topic") targetEntityType else null,
                            targetEntityId = if (targetType == "topic") targetEntityId.ifBlank { null } else null,
                            scheduledAt = scheduleIso,
                            link = link.ifBlank { null },
                            imageUrl = imageUrl.ifBlank { null },
                            platforms = filterPlatforms.toList().ifEmpty { null },
                            locales = filterLocale?.let { listOf(it) },
                            minAge = filterMinAge.toIntOrNull(),
                            maxAge = filterMaxAge.toIntOrNull()
                        ))
                        val countInfo = result.subscriberCount?.let { " ($it subscribers)" } ?: ""
                        message = (result.message ?: result.status) + countInfo
                        if (result.status != "error") {
                            titleAr = ""; titleEn = ""; bodyAr = ""; bodyEn = ""
                            scheduledAt = ""; link = ""; imageUrl = ""; entitySearch = ""
                            selectedEntityName = ""; selectedEntityNameAr = ""
                            targetEntityId = ""; targetLegacyId = null; linkAutoGenerated = true
                            filterPlatforms = emptySet(); filterLocale = null
                            filterMinAge = ""; filterMaxAge = ""; audienceEstimate = null
                        }
                    } catch (e: Exception) {
                        message = "Failed: ${e.message}"
                    } finally { sending = false }
                }
            },
            onCancel = { showConfirmation = false }
        )
    }

    // ── Form Layout: two columns ──
    Div({ style { display(DisplayStyle.Flex); gap(Spacing.lg); property("flex-wrap", "wrap") } }) {

        // Left column: form
        Div({
            style {
                property("flex", "1"); property("min-width", "350px"); property("max-width", "600px")
                padding(Spacing.lg); backgroundColor(Color(colors.surface))
                borderRadius(Spacing.sm); property("border", "1px solid ${colors.border}")
            }
        }) {
            // ── Section 1: Target ──
            SectionHeader(colors, "1. Target")

            Div({ style { display(DisplayStyle.Flex); gap(Spacing.lg); marginBottom(Spacing.md) } }) {
                RadioOption(colors, "Broadcast (all users)", targetType == "broadcast") { targetType = "broadcast" }
                RadioOption(colors, "Topic (entity)", targetType == "topic") { targetType = "topic" }
            }

            if (targetType == "topic") {
                // Entity type selector — legacy types first
                Div({ style { marginBottom(Spacing.sm) } }) {
                    FieldLabel(colors, "Entity Type")
                    Select({
                        onChange {
                            targetEntityType = it.value ?: "legacy_team"
                            entitySearch = ""; targetEntityId = ""; selectedEntityName = ""
                            selectedEntityNameAr = ""; targetLegacyId = null; linkAutoGenerated = true; link = ""
                        }
                        style { formInputStyle(colors) }
                        attr("value", targetEntityType)
                    }) {
                        // Legacy types first
                        Option("legacy_team", { if (targetEntityType == "legacy_team") attr("selected", "") }) { Text(legacyLabel("Team")) }
                        Option("legacy_league", { if (targetEntityType == "legacy_league") attr("selected", "") }) { Text(legacyLabel("League")) }
                        Option("legacy_player", { if (targetEntityType == "legacy_player") attr("selected", "") }) { Text(legacyLabel("Player")) }
                        // Modern types
                        if (SHOW_REVAMP) {
                            Option("team", { if (targetEntityType == "team") attr("selected", "") }) { Text("Team") }
                            Option("league", { if (targetEntityType == "league") attr("selected", "") }) { Text("League") }
                            Option("player", { if (targetEntityType == "player") attr("selected", "") }) { Text("Player") }
                            Option("fixture", { if (targetEntityType == "fixture") attr("selected", "") }) { Text("Fixture") }
                        }
                    }
                    Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); marginTop(Spacing.xs) } }) {
                        Text(when (targetEntityType) {
                            "legacy_team" -> "Sent to users who favorited the team"
                            "legacy_league" -> "Sent to users who favorited the league"
                            "legacy_player" -> "Sent to users who favorited the player"
                            "team" -> "Catalog teams (uses push subscriptions for targeting)"
                            "league" -> "Catalog leagues (uses push subscriptions for targeting)"
                            "player" -> "Catalog players (uses push subscriptions for targeting)"
                            "fixture" -> "Specific match/fixture (uses push subscriptions for targeting)"
                            else -> ""
                        })
                    }
                }

                // Entity search
                Div({ style { marginBottom(Spacing.sm); property("position", "relative") } }) {
                    FieldLabel(colors, "Search by Name")
                    Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                        value(entitySearch)
                        placeholder("Type to search...")
                        onInput { entitySearch = it.value; selectedEntityName = ""; targetEntityId = ""; targetLegacyId = null }
                        style { formInputStyle(colors) }
                    }

                    // Search dropdown
                    if (entityResults.isNotEmpty()) {
                        Div({
                            style {
                                property("position", "absolute"); property("top", "100%"); property("left", "0"); property("right", "0")
                                property("z-index", "10"); backgroundColor(Color(colors.surface))
                                property("border", "1px solid ${colors.border}"); borderRadius(0.px, 0.px, Spacing.xs, Spacing.xs)
                                property("max-height", "300px"); overflow("auto"); property("box-shadow", Shadow.md)
                            }
                        }) {
                            entityResults.forEach { entity ->
                                Div({
                                    style {
                                        padding(Spacing.sm, Spacing.md); cursor("pointer"); color(Color(colors.text))
                                        property("border-bottom", "1px solid ${colors.border}")
                                    }
                                    onClick {
                                        targetEntityType = entity.entityType
                                        targetEntityId = entity.entityId
                                        targetLegacyId = entity.legacyId
                                        entitySearch = entity.nameEn
                                        selectedEntityName = entity.nameEn
                                        selectedEntityNameAr = entity.nameAr ?: ""
                                        entityResults = emptyList()
                                        linkAutoGenerated = true
                                        link = generateLink(entity.entityType, entity.entityId, entity.legacyId)
                                    }
                                }) {
                                    Div({ style { display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.sm) } }) {
                                        EntityTypeBadge(colors, entity.entityType)
                                        Span({ style { fontWeight("600") } }) { Text(entity.nameEn) }
                                        entity.legacyId?.let { lid ->
                                            Span({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                                                Text("(ID: $lid)")
                                            }
                                        }
                                    }
                                    entity.nameAr?.let { nameAr ->
                                        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); paddingLeft(Spacing.lg) } }) {
                                            Text(nameAr)
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Selected entity display
                    if (selectedEntityName.isNotBlank() && targetEntityId.isNotBlank()) {
                        Div({
                            style {
                                marginTop(Spacing.sm); padding(Spacing.sm, Spacing.md)
                                backgroundColor(Color(colors.background)); borderRadius(Spacing.xs)
                                property("border", "1px solid ${colors.border}")
                            }
                        }) {
                            Div({ style { display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.sm); marginBottom(Spacing.xs) } }) {
                                EntityTypeBadge(colors, targetEntityType)
                                Span({ style { fontWeight("600"); color(Color(colors.text)) } }) { Text(selectedEntityName) }
                            }
                            if (selectedEntityNameAr.isNotBlank()) {
                                Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginBottom(Spacing.xs) } }) {
                                    Text(selectedEntityNameAr)
                                }
                            }
                            Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                                val legacyId = targetLegacyId
                                if (SHOW_REVAMP || legacyId == null) {
                                    Text("ID: ${targetEntityId.take(8)}...")
                                    legacyId?.let { Text(" | Legacy ID: $it") }
                                } else {
                                    Text("ID: $legacyId")
                                }
                            }
                        }
                    }
                }
            }

            // ── Section 2: Content ──
            SectionHeader(colors, "2. Content")

            // Side-by-side AR/EN titles
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); marginBottom(Spacing.sm) } }) {
                Div({ style { property("flex", "1") } }) {
                    InputField(colors, "Title (Arabic) *", titleAr) { titleAr = it }
                }
                Div({ style { property("flex", "1") } }) {
                    InputField(colors, "Title (English) *", titleEn) { titleEn = it }
                }
            }

            // Side-by-side AR/EN bodies
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); marginBottom(Spacing.sm) } }) {
                Div({ style { property("flex", "1") } }) {
                    TextAreaField(colors, "Body (Arabic)", bodyAr) { bodyAr = it }
                }
                Div({ style { property("flex", "1") } }) {
                    TextAreaField(colors, "Body (English)", bodyEn) { bodyEn = it }
                }
            }

            // ── Section 3: Options ──
            SectionHeader(colors, "3. Options")

            // Link field with auto-generation indicator
            Div({ style { marginBottom(Spacing.sm) } }) {
                Div({ style { display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.sm); marginBottom(Spacing.xs) } }) {
                    FieldLabel(colors, "Deep Link")
                    if (linkAutoGenerated && link.isNotBlank()) {
                        Span({ style { fontSize(Typography.xs); color(Color(colors.success)); fontWeight("500") } }) {
                            Text("(auto-generated)")
                        }
                    }
                }
                Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                    value(link)
                    placeholder("/home/sports/team/123")
                    onInput { link = it.value; linkAutoGenerated = false }
                    style { formInputStyle(colors) }
                }
                if (link.isNotBlank()) {
                    Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); marginTop(Spacing.xs) } }) {
                        Text("Users will navigate to: $link")
                    }
                }
            }

            // Image URL field
            Div({ style { marginBottom(Spacing.sm) } }) {
                FieldLabel(colors, "Image URL (optional)")
                Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                    value(imageUrl)
                    placeholder("https://cdn.example.com/image.jpg")
                    onInput { imageUrl = it.value }
                    style { formInputStyle(colors) }
                }
                if (imageUrl.isNotBlank()) {
                    Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); marginTop(Spacing.xs) } }) {
                        Text("Image will appear in the notification on all platforms")
                    }
                }
            }

            // Schedule
            Div({ style { marginBottom(Spacing.md) } }) {
                Div({
                    style { color(Color(colors.text)); cursor("pointer"); display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.xs) }
                    onClick { scheduleEnabled = !scheduleEnabled; if (!scheduleEnabled) scheduledAt = "" }
                }) {
                    Input(type = org.jetbrains.compose.web.attributes.InputType.Checkbox) {
                        checked(scheduleEnabled)
                        onChange { scheduleEnabled = it.value }
                    }
                    Text(" Schedule for later")
                }
                if (scheduleEnabled) {
                    Div({ style { marginTop(Spacing.sm) } }) {
                        Input(type = org.jetbrains.compose.web.attributes.InputType.DateTimeLocal) {
                            value(scheduledAt)
                            onInput { scheduledAt = it.value }
                            style { formInputStyle(colors) }
                        }
                        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); marginTop(Spacing.xs) } }) {
                            Text("Time is in UTC. ")
                            if (scheduledAt.isNotBlank()) {
                                Text("Egypt time: ${utcToEgypt(scheduledAt)}")
                            }
                        }
                    }
                }
            }

            // ── Section 4: Audience Filters (broadcast only) ──
            if (targetType == "broadcast") {
            SectionHeader(colors, "4. Audience Filters (optional)")

            // Platform checkboxes
            Div({ style { marginBottom(Spacing.sm) } }) {
                FieldLabel(colors, "Platforms")
                Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); property("flex-wrap", "wrap") } }) {
                    listOf("fcm_android" to "Android", "fcm_ios" to "iOS", "fcm_web" to "Web").forEach { (value, label) ->
                        Div({
                            style { color(Color(colors.text)); cursor("pointer"); display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.xs) }
                            onClick {
                                filterPlatforms = if (value in filterPlatforms) filterPlatforms - value else filterPlatforms + value
                            }
                        }) {
                            Input(type = org.jetbrains.compose.web.attributes.InputType.Checkbox) {
                                checked(value in filterPlatforms)
                                onChange {
                                    filterPlatforms = if (it.value) filterPlatforms + value else filterPlatforms - value
                                }
                            }
                            Text(" $label")
                        }
                    }
                }
            }

            // Locale filter
            Div({ style { marginBottom(Spacing.sm) } }) {
                FieldLabel(colors, "Locale")
                Select({
                    onChange { filterLocale = it.value?.ifBlank { null } }
                    style { formInputStyle(colors); width(200.px) }
                }) {
                    Option("") { Text("All Locales") }
                    Option("ar") { Text("Arabic (ar)") }
                    Option("en") { Text("English (en)") }
                }
            }

            // Age range
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); marginBottom(Spacing.md) } }) {
                Div({ style { property("flex", "1") } }) {
                    FieldLabel(colors, "Min Age")
                    Input(type = org.jetbrains.compose.web.attributes.InputType.Number) {
                        value(filterMinAge)
                        placeholder("e.g. 18")
                        onInput { filterMinAge = it.value?.toString() ?: "" }
                        style { formInputStyle(colors) }
                    }
                }
                Div({ style { property("flex", "1") } }) {
                    FieldLabel(colors, "Max Age")
                    Input(type = org.jetbrains.compose.web.attributes.InputType.Number) {
                        value(filterMaxAge)
                        placeholder("e.g. 35")
                        onInput { filterMaxAge = it.value?.toString() ?: "" }
                        style { formInputStyle(colors) }
                    }
                }
            }

            } // end broadcast-only audience filters

            // Audience estimate display (for both broadcast and topic)
            if (audienceEstimate != null) {
                Div({
                    style {
                        padding(Spacing.sm, Spacing.md)
                        backgroundColor(Color(colors.background)); borderRadius(Spacing.xs)
                        property("border", "1px solid ${colors.border}")
                        marginBottom(Spacing.md)
                    }
                }) {
                    Div({ style { display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.sm); marginBottom(Spacing.xs) } }) {
                        Span({ style { fontSize(Typography.lg); fontWeight("700"); color(Color(colors.primary)) } }) {
                            Text(audienceEstimate.toString())
                        }
                        Span({ style { color(Color(colors.textMuted)); fontSize(Typography.sm) } }) {
                            Text(if (estimating) "estimating..." else "estimated recipients")
                        }
                    }
                    Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                        val explanation = when {
                            targetType == "broadcast" && filterPlatforms.isNotEmpty() || filterLocale != null || filterMinAge.isNotBlank() || filterMaxAge.isNotBlank() ->
                                "Devices matching selected filters (platform, locale, age)"
                            targetType == "broadcast" ->
                                "Total registered devices across all platforms"
                            targetType == "topic" && targetEntityType.startsWith("legacy_") ->
                                "Users who favorited this ${targetEntityType.removePrefix("legacy_")} (sent to all their devices)"
                            targetType == "topic" && targetEntityId.isNotBlank() ->
                                "Users subscribed to this ${targetEntityType}'s push notifications"
                            targetType == "topic" ->
                                "Select an entity to see subscriber count"
                            else -> ""
                        }
                        if (explanation.isNotBlank()) Text(explanation)
                    }
                }
            }

            // Send button
            Button({
                style {
                    primaryButtonStyle(colors, sending || titleAr.isBlank() || titleEn.isBlank())
                    width(100.percent)
                }
                onClick {
                    if (sending || titleAr.isBlank() || titleEn.isBlank()) return@onClick
                    showConfirmation = true
                }
            }) {
                Text(
                    when {
                        sending -> "Sending..."
                        scheduleEnabled -> "Review & Schedule"
                        else -> "Review & Send"
                    }
                )
            }

            // Status message
            message?.let { msg ->
                val msgColor = when {
                    msg.startsWith("Failed") || msg.startsWith("error") -> colors.danger
                    msg.contains("No subscribers") -> colors.warning
                    msg.contains("Pre-filled") -> colors.info
                    else -> colors.success
                }
                Div({ style { marginTop(Spacing.sm); padding(Spacing.sm, Spacing.md); color(Color(msgColor)); fontWeight("500"); backgroundColor(Color(colors.background)); borderRadius(Spacing.xs) } }) {
                    Text(msg)
                }
            }
        }

        // Right column: preview
        Div({
            style {
                property("flex", "1"); property("min-width", "280px"); property("max-width", "380px")
                padding(Spacing.lg); backgroundColor(Color(colors.surface))
                borderRadius(Spacing.sm); property("border", "1px solid ${colors.border}")
            }
        }) {
            Div({ style { fontWeight("600"); color(Color(colors.text)); marginBottom(Spacing.md); fontSize(Typography.lg) } }) {
                Text("Preview")
            }

            // Phone notification mock
            NotificationPreview(colors, "EN", titleEn, bodyEn, imageUrl)
            Div({ style { height(Spacing.md) } }) {}
            NotificationPreview(colors, "AR", titleAr, bodyAr, imageUrl)

            // Summary
            if (targetType == "topic" && selectedEntityName.isNotBlank()) {
                Div({ style { marginTop(Spacing.lg) } }) {
                    SummaryRow(colors, "Target", "$selectedEntityName (${entityTypeLabel(targetEntityType)})")
                    targetLegacyId?.let { SummaryRow(colors, legacyLabel("ID"), it) }
                    if (link.isNotBlank()) SummaryRow(colors, "Link", link)
                }
            } else if (targetType == "broadcast") {
                Div({ style { marginTop(Spacing.lg) } }) {
                    SummaryRow(colors, "Target", "All users (broadcast)")
                    if (link.isNotBlank()) SummaryRow(colors, "Link", link)
                }
            }
        }
    }
}

// ─── Notification Preview Mock ───

@Composable
private fun NotificationPreview(colors: AppColors, locale: String, title: String, body: String, imageUrl: String) {
    // Firebase Console-style phone frame
    Div({
        style {
            width(300.px)
            property("margin", "0 auto")
            backgroundColor(Color("#1a1d21"))
            borderRadius(24.px)
            property("border", "3px solid #333")
            overflow("hidden")
            property("box-shadow", Shadow.lg)
        }
    }) {
        // Status bar
        Div({
            style {
                display(DisplayStyle.Flex)
                property("justify-content", "space-between")
                property("align-items", "center")
                padding(Spacing.sm, Spacing.md)
                fontSize(Typography.xs)
                color(Color("#666"))
            }
        }) {
            Span { Text("9:41") }
            Span { Text(locale) }
        }

        // Notification card
        Div({
            style {
                property("margin", "0 ${Spacing.md} ${Spacing.md}")
                backgroundColor(Color("#2a2d32"))
                borderRadius(12.px)
                overflow("hidden")
                if (locale == "AR") property("direction", "rtl")
            }
        }) {
            // App icon row
            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("align-items", "center")
                    gap(Spacing.sm)
                    padding(Spacing.sm, Spacing.md)
                }
            }) {
                // App icon
                Div({
                    style {
                        width(18.px); height(18.px)
                        borderRadius(4.px)
                        backgroundColor(Color(colors.primary))
                    }
                }) {}
                Span({ style { fontSize(Typography.xs); color(Color("#888")) } }) { Text("Twist Sports") }
                Span({ style { fontSize(Typography.xs); color(Color("#555")); property("margin-${if (locale == "AR") "right" else "left"}", "auto") } }) { Text("now") }
            }

            // Title + Body
            Div({ style { padding(0.px, Spacing.md, Spacing.sm) } }) {
                Div({ style { fontWeight("600"); color(Color("#e1ebf3")); fontSize(Typography.sm) } }) {
                    Text(title.ifBlank { "(no title)" })
                }
                if (body.isNotBlank()) {
                    Div({ style { color(Color("#888")); fontSize(Typography.sm); marginTop(2.px) } }) {
                        Text(body)
                    }
                }
            }

            // Image
            if (imageUrl.isNotBlank()) {
                Img(src = imageUrl, alt = "Notification image") {
                    style {
                        width(100.percent)
                        property("max-height", "180px")
                        property("object-fit", "cover")
                        display(DisplayStyle.Block)
                    }
                    attr("onerror", "this.style.display='none'")
                }
            }
        }

        // Home indicator
        Div({
            style {
                display(DisplayStyle.Flex); property("justify-content", "center"); paddingBottom(Spacing.sm)
            }
        }) {
            Div({
                style {
                    width(100.px); height(4.px); borderRadius(2.px)
                    backgroundColor(Color("#444"))
                }
            }) {}
        }
    }
}

// ─── Confirmation Dialog ───

@Composable
private fun ConfirmationDialog(
    colors: AppColors,
    targetType: String,
    entityType: String,
    entityName: String,
    entityNameAr: String,
    legacyId: String?,
    titleAr: String,
    titleEn: String,
    bodyAr: String,
    bodyEn: String,
    link: String,
    imageUrl: String,
    scheduleEnabled: Boolean,
    scheduledAt: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    Div({ style { confirmationOverlayStyle(colors) }; onClick { onCancel() } }) {
        Div({
            style {
                confirmationContentStyle(colors)
                property("max-width", "500px"); width(90.vw)
            }
            onClick { it.stopPropagation() }
        }) {
            Div({ style { fontWeight("700"); fontSize(Typography.xl); marginBottom(Spacing.lg); color(Color(colors.text)) } }) {
                Text(if (scheduleEnabled) "Confirm Schedule" else "Confirm Send")
            }

            // Target summary
            Div({
                style {
                    padding(Spacing.md); backgroundColor(Color(colors.background))
                    borderRadius(Spacing.sm); marginBottom(Spacing.md)
                    property("border", "1px solid ${colors.border}")
                }
            }) {
                if (targetType == "broadcast") {
                    Div({ style { display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.sm) } }) {
                        Span({ style { badgeStyle(colors.danger, "#fff") } }) { Text("BROADCAST") }
                        Span({ style { fontWeight("600"); color(Color(colors.text)) } }) { Text("All users") }
                    }
                } else {
                    Div({ style { display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.sm); marginBottom(Spacing.xs) } }) {
                        EntityTypeBadge(colors, entityType)
                        Span({ style { fontWeight("600"); color(Color(colors.text)) } }) { Text(entityName) }
                    }
                    if (entityNameAr.isNotBlank()) {
                        Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginBottom(Spacing.xs) } }) { Text(entityNameAr) }
                    }
                    legacyId?.let {
                        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) { Text("${legacyLabel("ID")}: $it") }
                    }
                }
            }

            // Content preview
            SummaryRow(colors, "Title (EN)", titleEn)
            SummaryRow(colors, "Title (AR)", titleAr)
            if (bodyEn.isNotBlank()) SummaryRow(colors, "Body (EN)", bodyEn)
            if (bodyAr.isNotBlank()) SummaryRow(colors, "Body (AR)", bodyAr)
            if (link.isNotBlank()) SummaryRow(colors, "Link", link)
            if (imageUrl.isNotBlank()) SummaryRow(colors, "Image", imageUrl)
            if (scheduleEnabled && scheduledAt.isNotBlank()) {
                SummaryRow(colors, "Scheduled (UTC)", scheduledAt.replace("T", " ") + " UTC")
                SummaryRow(colors, "Scheduled (EGY)", utcToEgypt(scheduledAt) + " (Egypt)")
            }

            // Action buttons
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); marginTop(Spacing.lg); property("justify-content", "flex-end") } }) {
                Button({
                    style { secondaryButtonStyle(colors) }
                    onClick { onCancel() }
                }) { Text("Cancel") }
                Button({
                    style {
                        if (targetType == "broadcast") dangerButtonStyle(colors)
                        else primaryButtonStyle(colors)
                    }
                    onClick { onConfirm() }
                }) {
                    Text(
                        when {
                            scheduleEnabled -> "Schedule Now"
                            targetType == "broadcast" -> "Send to ALL"
                            else -> "Send"
                        }
                    )
                }
            }
        }
    }
}

// ═══════════════════════════════════════════════════════════
//  ACTIVITY TAB (merged History + Scheduled + Analytics strip)
// ═══════════════════════════════════════════════════════════

private enum class ActivityView(val label: String) {
    HISTORY("History"),
    SCHEDULED("Scheduled")
}

@Composable
private fun ActivityTab(apiClient: AdminApiClient, onDuplicate: (AdminNotificationHistoryResponse) -> Unit) {
    val colors = AppTheme.colors
    var view by remember { mutableStateOf(ActivityView.HISTORY) }

    // Analytics strip
    AnalyticsStrip(apiClient)

    // Sub-navigation
    Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.md) } }) {
        ActivityView.entries.forEach { v ->
            Button({
                style {
                    padding(Spacing.sm, Spacing.lg)
                    backgroundColor(Color(if (view == v) colors.primary else colors.surface))
                    color(Color(if (view == v) "#fff" else colors.text))
                    borderRadius(Spacing.sm)
                    property("border", if (view == v) "none" else "1px solid ${colors.border}")
                    cursor("pointer"); fontWeight("600"); fontSize(Typography.sm)
                }
                onClick { view = v }
            }) { Text(v.label) }
        }
    }

    when (view) {
        ActivityView.HISTORY -> HistorySection(apiClient, onDuplicate)
        ActivityView.SCHEDULED -> ScheduledSection(apiClient)
    }
}

@Composable
private fun AnalyticsStrip(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    var state by remember { mutableStateOf<UiState<AdminNotificationAnalyticsResponse>>(UiState.Loading) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        scope.launch {
            try {
                state = UiState.Success(apiClient.getNotificationAnalytics())
            } catch (e: Exception) {
                state = UiState.Error("Failed to load analytics")
            }
        }
    }

    when (val s = state) {
        is UiState.Loading -> {}
        is UiState.Error -> {}
        is UiState.Success -> {
            val data = s.data
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.lg); property("flex-wrap", "wrap") } }) {
                MiniStatCard(colors, "Sent", data.totalSent.toString(), colors.primary)
                MiniStatCard(colors, "Delivered", data.totalDelivered.toString(), colors.success)
                MiniStatCard(colors, "Failed", data.totalFailed.toString(), colors.danger)
                MiniStatCard(colors, "Pending", data.totalPending.toString(), colors.warning)
                MiniStatCard(colors, "Today", data.sentToday.toString(), colors.info)
                MiniStatCard(colors, "This Week", data.sentThisWeek.toString(), colors.info)
            }
        }
    }
}

@Composable
private fun MiniStatCard(colors: AppColors, label: String, value: String, accentColor: String) {
    Div({
        style {
            padding(Spacing.sm, Spacing.md)
            backgroundColor(Color(colors.surface))
            borderRadius(Spacing.xs)
            property("border", "1px solid ${colors.border}")
            property("border-left", "3px solid $accentColor")
            property("min-width", "100px"); property("flex", "1")
        }
    }) {
        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) { Text(label) }
        Div({ style { fontSize(Typography.lg); fontWeight("700"); color(Color(colors.text)) } }) { Text(value) }
    }
}

@Composable
private fun HistorySection(apiClient: AdminApiClient, onDuplicate: (AdminNotificationHistoryResponse) -> Unit) {
    val colors = AppTheme.colors
    var state by remember { mutableStateOf<UiState<OffsetPage<AdminNotificationHistoryResponse>>>(UiState.Loading) }
    var statusFilter by remember { mutableStateOf<String?>(null) }
    var offset by remember { mutableStateOf(0) }
    var refreshKey by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(statusFilter, offset, refreshKey) {
        state = UiState.Loading
        scope.launch {
            try {
                state = UiState.Success(apiClient.getNotificationHistory(statusFilter, null, offset))
            } catch (e: Exception) {
                state = UiState.Error("Failed to load notification history")
            }
        }
    }

    Div({ style { marginBottom(Spacing.md) } }) {
        Select({
            onChange { statusFilter = it.value?.ifBlank { null }; offset = 0 }
            style { formInputStyle(colors); width(200.px) }
        }) {
            Option("") { Text("All Statuses") }
            Option("pending") { Text("Pending") }
            Option("queued") { Text("Queued") }
            Option("sent") { Text("Sent") }
            Option("delivered") { Text("Delivered") }
            Option("failed") { Text("Failed") }
        }
    }

    when (val s = state) {
        is UiState.Loading -> Div({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> Div({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            DataTable(
                rows = s.data.data,
                columns = listOf(
                    Column("Title (EN)") { Text(it.titleEn) },
                    Column("Target") {
                        Div({ style { display(DisplayStyle.Flex); property("flex-direction", "column"); gap(2.px) } }) {
                            Div({ style { display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.xs) } }) {
                                EntityTypeBadge(colors, it.entityType)
                                Text(it.type)
                            }
                            if (it.entityType != "broadcast") {
                                Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                                    Text(it.entityId.take(12) + if (it.entityId.length > 12) "..." else "")
                                }
                            }
                            it.topicName?.let { topic ->
                                Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                                    Text(topic.take(30) + if (topic.length > 30) "..." else "")
                                }
                            }
                        }
                    },
                    Column("Link") {
                        it.link?.let { link ->
                            Div({ style { fontSize(Typography.xs); color(Color(colors.primary)); property("max-width", "150px"); property("word-break", "break-all") } }) {
                                Text(link)
                            }
                        }
                    },
                    Column("Image") {
                        it.imageUrl?.let { url ->
                            Img(src = url, alt = "") {
                                style { width(40.px); height(40.px); property("object-fit", "cover"); borderRadius(Spacing.xs) }
                            }
                        }
                    },
                    Column("Status") {
                        Span({
                            style {
                                fontWeight("600")
                                color(Color(when (it.deliveryStatus) {
                                    "sent", "delivered" -> colors.success
                                    "failed" -> colors.danger
                                    "pending", "queued" -> colors.warning
                                    else -> colors.textMuted
                                }))
                            }
                        }) { Text(it.deliveryStatus) }
                    },
                    Column("Sent By") { Text(it.adminSenderId ?: "system") },
                    Column("Created") {
                        Div {
                            Div { Text(it.createdAtUtc.take(16).replace("T", " ")) }
                            Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                                Text(utcToEgypt(it.createdAtUtc.take(16)) + " EGY")
                            }
                        }
                    },
                    Column("") { hist ->
                        Div({ style { display(DisplayStyle.Flex); gap(Spacing.xs) } }) {
                            Button({
                                style {
                                    padding(Spacing.xs, Spacing.sm)
                                    backgroundColor(Color(colors.surface))
                                    color(Color(colors.primary))
                                    borderRadius(Spacing.xs)
                                    property("border", "1px solid ${colors.border}")
                                    cursor("pointer"); fontSize(Typography.xs); fontWeight("600")
                                }
                                onClick { onDuplicate(hist) }
                            }) { Text("Duplicate") }
                            if (hist.deliveryStatus == "failed") {
                                Button({
                                    style {
                                        padding(Spacing.xs, Spacing.sm)
                                        backgroundColor(Color(colors.warning))
                                        color(Color("#1a1d21"))
                                        borderRadius(Spacing.xs)
                                        property("border", "none")
                                        cursor("pointer"); fontSize(Typography.xs); fontWeight("600")
                                    }
                                    onClick {
                                        scope.launch {
                                            try {
                                                apiClient.retryNotification(hist.id)
                                                refreshKey++
                                            } catch (_: Exception) {}
                                        }
                                    }
                                }) { Text("Retry") }
                            }
                        }
                    }
                ),
                offset = offset,
                total = s.data.total,
                onPageChange = { offset = it }
            )
        }
    }
}

@Composable
private fun ScheduledSection(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    var state by remember { mutableStateOf<UiState<OffsetPage<AdminScheduledNotificationResponse>>>(UiState.Loading) }
    var offset by remember { mutableStateOf(0) }
    var refreshKey by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(offset, refreshKey) {
        state = UiState.Loading
        scope.launch {
            try {
                state = UiState.Success(apiClient.getScheduledNotifications(offset))
            } catch (e: Exception) {
                state = UiState.Error("Failed to load scheduled notifications")
            }
        }
    }

    when (val s = state) {
        is UiState.Loading -> Div({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> Div({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            DataTable(
                rows = s.data.data,
                columns = listOf(
                    Column("Title (EN)") { Text(it.titleEn) },
                    Column("Target") { Text("${it.targetType}${it.targetValue?.let { v -> ": $v" } ?: ""}") },
                    Column("Scheduled") {
                        Div {
                            Div { Text(it.scheduledAtUtc.take(16).replace("T", " ") + " UTC") }
                            Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                                Text(utcToEgypt(it.scheduledAtUtc.take(16)) + " Egypt")
                            }
                            if (it.status == "pending") {
                                val remaining = remainingTime(it.scheduledAtUtc)
                                Div({ style { fontSize(Typography.xs); color(Color(if (remaining.startsWith("-")) colors.danger else colors.primary)); fontWeight("600") } }) {
                                    Text(remaining)
                                }
                            }
                        }
                    },
                    Column("Status") {
                        Span({
                            style {
                                fontWeight("600")
                                color(Color(when (it.status) {
                                    "pending" -> colors.warning
                                    "sent" -> colors.success
                                    "cancelled" -> colors.danger
                                    else -> colors.textMuted
                                }))
                            }
                        }) { Text(it.status) }
                    },
                    Column("") { scheduled ->
                        if (scheduled.status == "pending") {
                            Button({
                                style { dangerButtonStyle(colors); padding(Spacing.xs, Spacing.sm); fontSize(Typography.xs) }
                                onClick { scope.launch { try { apiClient.cancelScheduledNotification(scheduled.id); refreshKey++ } catch (_: Exception) {} } }
                            }) { Text("Cancel") }
                        }
                    }
                ),
                offset = offset,
                total = s.data.total,
                onPageChange = { offset = it }
            )
        }
    }
}

// ═══════════════════════════════════════════════════════════
//  AUDIENCE TAB (merged Topics + Devices)
// ═══════════════════════════════════════════════════════════

private enum class AudienceView(val label: String) {
    TOPICS("Topics"),
    DEVICES("Devices")
}

@Composable
private fun AudienceTab(apiClient: AdminApiClient, onSendToTopic: (String, String, String?) -> Unit) {
    val colors = AppTheme.colors
    var view by remember { mutableStateOf(AudienceView.TOPICS) }

    Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.md) } }) {
        AudienceView.entries.forEach { v ->
            Button({
                style {
                    padding(Spacing.sm, Spacing.lg)
                    backgroundColor(Color(if (view == v) colors.primary else colors.surface))
                    color(Color(if (view == v) "#fff" else colors.text))
                    borderRadius(Spacing.sm)
                    property("border", if (view == v) "none" else "1px solid ${colors.border}")
                    cursor("pointer"); fontWeight("600"); fontSize(Typography.sm)
                }
                onClick { view = v }
            }) { Text(v.label) }
        }
    }

    when (view) {
        AudienceView.TOPICS -> TopicsSection(apiClient, onSendToTopic)
        AudienceView.DEVICES -> DevicesSection(apiClient)
    }
}

@Composable
private fun TopicsSection(apiClient: AdminApiClient, onSendToTopic: (String, String, String?) -> Unit) {
    val colors = AppTheme.colors
    var state by remember { mutableStateOf<UiState<OffsetPage<AdminTopicResponse>>>(UiState.Loading) }
    var search by remember { mutableStateOf("") }
    var typeFilter by remember { mutableStateOf<String?>(null) }
    var offset by remember { mutableStateOf(0) }
    var expandedTopic by remember { mutableStateOf<Pair<String, String>?>(null) }
    var subscribers by remember { mutableStateOf<List<AdminTopicSubscriberResponse>>(emptyList()) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(search, typeFilter, offset) {
        state = UiState.Loading
        scope.launch {
            try {
                state = UiState.Success(apiClient.getNotificationTopics(search.ifBlank { null }, typeFilter, offset))
            } catch (e: Exception) {
                state = UiState.Error("Failed to load topics")
            }
        }
    }

    Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); marginBottom(Spacing.md) } }) {
        Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
            value(search)
            placeholder("Search by name or ID...")
            onInput { search = it.value; offset = 0 }
            style { formInputStyle(colors); property("flex", "1") }
        }
        Select({
            onChange { typeFilter = it.value?.ifBlank { null }; offset = 0 }
            style { formInputStyle(colors); width(180.px) }
        }) {
            Option("") { Text("All Types") }
            Option("team") { Text("Team") }
            Option("league") { Text("League") }
            Option("player") { Text("Player") }
            Option("fixture") { Text("Fixture") }
        }
    }

    // Topic type descriptions
    Div({
        style {
            padding(Spacing.sm, Spacing.md); marginBottom(Spacing.md)
            backgroundColor(Color(colors.background)); borderRadius(Spacing.xs)
            property("border", "1px solid ${colors.border}"); fontSize(Typography.xs); color(Color(colors.textMuted))
        }
    }) {
        Div({ style { fontWeight("600"); marginBottom(Spacing.xs); color(Color(colors.text)) } }) { Text("Topic Types:") }
        Div { Text("Team — users subscribed to a team's updates (match results, lineups, transfers)") }
        Div { Text("League — users subscribed to a league's updates (standings, fixtures, results)") }
        Div { Text("Player — users subscribed to a player's updates (stats, transfers, injuries)") }
        Div { Text("Fixture — users subscribed to a specific match (score updates, events)") }
    }

    when (val s = state) {
        is UiState.Loading -> Div({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> Div({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            if (s.data.data.isEmpty()) {
                Div({ style { color(Color(colors.textMuted)) } }) { Text("No topics found") }
            } else {
                Div({ style { backgroundColor(Color(colors.surface)); borderRadius(Spacing.sm); property("border", "1px solid ${colors.border}"); overflow("hidden") } }) {
                    Table({ style { width(100.percent); property("border-collapse", "collapse") } }) {
                        Thead {
                            Tr({ style { backgroundColor(Color(colors.surfaceHover)); property("border-bottom", "1px solid ${colors.border}") } }) {
                                listOf("Type", "Name (EN)", "Name (AR)", "Entity ID", "Subscribers", "").forEach { h ->
                                    Th({ style { tableHeaderStyle(colors) } }) { Text(h) }
                                }
                            }
                        }
                        Tbody {
                            s.data.data.forEach { topic ->
                                val isExpanded = expandedTopic == (topic.entityType to topic.entityId)
                                Tr({
                                    style { property("border-bottom", "1px solid ${colors.border}"); cursor("pointer"); if (isExpanded) backgroundColor(Color(colors.background)) }
                                    onClick {
                                        if (isExpanded) {
                                            expandedTopic = null; subscribers = emptyList()
                                        } else {
                                            expandedTopic = topic.entityType to topic.entityId
                                            scope.launch {
                                                try { subscribers = apiClient.getTopicSubscribers(topic.entityType, topic.entityId).data } catch (_: Exception) { subscribers = emptyList() }
                                            }
                                        }
                                    }
                                }) {
                                    Td({ style { padding(Spacing.sm, Spacing.md) } }) { EntityTypeBadge(colors, topic.entityType) }
                                    Td({ style { padding(Spacing.sm, Spacing.md); color(Color(colors.text)) } }) { Text(topic.entityNameEn ?: "-") }
                                    Td({ style { padding(Spacing.sm, Spacing.md); color(Color(colors.text)) } }) { Text(topic.entityNameAr ?: "-") }
                                    Td({ style { padding(Spacing.sm, Spacing.md); color(Color(colors.text)); fontSize(Typography.xs); property("user-select", "all"); property("word-break", "break-all") } }) { Text(topic.entityId) }
                                    Td({ style { padding(Spacing.sm, Spacing.md); color(Color(colors.text)); fontWeight("600") } }) { Text(topic.subscriberCount.toString()) }
                                    Td({ style { padding(Spacing.sm, Spacing.md) } }) {
                                        Button({
                                            style {
                                                padding(Spacing.xs, Spacing.sm)
                                                backgroundColor(Color(colors.primary))
                                                color(Color("#fff"))
                                                borderRadius(Spacing.xs)
                                                property("border", "none")
                                                cursor("pointer"); fontSize(Typography.xs); fontWeight("600")
                                            }
                                            onClick {
                                                it.stopPropagation()
                                                onSendToTopic(topic.entityType, topic.entityId, topic.entityNameEn)
                                            }
                                        }) { Text("Send") }
                                    }
                                }
                                if (isExpanded && subscribers.isNotEmpty()) {
                                    Tr {
                                        Td({ attr("colspan", "6"); style { padding(Spacing.sm, Spacing.lg); backgroundColor(Color(colors.background)) } }) {
                                            Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); marginBottom(Spacing.xs) } }) { Text("Subscribers:") }
                                            subscribers.forEach { sub ->
                                                Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); padding(Spacing.xs, 0.px); color(Color(colors.text)); fontSize(Typography.sm) } }) {
                                                    Span({ style { property("min-width", "200px") } }) { Text(sub.twistUserId) }
                                                    Span({ style { color(Color(colors.textMuted)) } }) { Text("${sub.deviceCount} devices") }
                                                    Span({ style { color(Color(colors.textMuted)) } }) { Text(sub.events.joinToString(", ")) }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                if (s.data.total > 50) {
                    Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginTop(Spacing.md); property("justify-content", "center") } }) {
                        if (offset > 0) {
                            Button({
                                style { paginationButtonStyle(colors, false) }
                                onClick { offset = (offset - 50).coerceAtLeast(0) }
                            }) { Text("Previous") }
                        }
                        if (offset + 50 < s.data.total) {
                            Button({
                                style { paginationButtonStyle(colors, false) }
                                onClick { offset += 50 }
                            }) { Text("Next") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DevicesSection(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    var state by remember { mutableStateOf<UiState<OffsetPage<AdminDeviceResponse>>>(UiState.Loading) }
    var search by remember { mutableStateOf("") }
    var offset by remember { mutableStateOf(0) }
    var expandedUser by remember { mutableStateOf<String?>(null) }
    var userSubs by remember { mutableStateOf<List<AdminUserSubscriptionResponse>>(emptyList()) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(search, offset) {
        state = UiState.Loading
        scope.launch {
            try {
                state = UiState.Success(apiClient.getNotificationDevices(search.ifBlank { null }, offset))
            } catch (e: Exception) {
                state = UiState.Error("Failed to load devices")
            }
        }
    }

    Div({ style { marginBottom(Spacing.md) } }) {
        Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
            value(search)
            placeholder("Search by user ID...")
            onInput { search = it.value; offset = 0 }
            style { formInputStyle(colors) }
        }
    }

    when (val s = state) {
        is UiState.Loading -> Div({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> Div({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            DataTable(
                rows = s.data.data,
                columns = listOf(
                    Column("User ID") { device ->
                        Span({
                            style { cursor("pointer"); color(Color(colors.primary)); property("text-decoration", "underline") }
                            onClick {
                                val userId = device.twistUserId
                                if (expandedUser == userId) {
                                    expandedUser = null; userSubs = emptyList()
                                } else {
                                    expandedUser = userId
                                    scope.launch {
                                        try { userSubs = apiClient.getUserSubscriptions(userId) } catch (_: Exception) { userSubs = emptyList() }
                                    }
                                }
                            }
                        }) { Text(device.twistUserId) }
                    },
                    Column("Platform") { Text(it.platform) },
                    Column("Locale") { Text(it.locale) },
                    Column("Status") {
                        Span({ style { color(Color(if (it.status == "active") colors.success else colors.danger)); fontWeight("600") } }) { Text(it.status) }
                    },
                    Column("Registered") { Text(it.createdAtUtc.take(10)) }
                ),
                offset = offset,
                total = s.data.total,
                onPageChange = { offset = it }
            )

            if (expandedUser != null && userSubs.isNotEmpty()) {
                Div({
                    style {
                        marginTop(Spacing.sm); padding(Spacing.md)
                        backgroundColor(Color(colors.surface)); borderRadius(Spacing.sm)
                        property("border", "1px solid ${colors.primary}")
                    }
                }) {
                    Div({ style { fontWeight("600"); color(Color(colors.text)); marginBottom(Spacing.sm) } }) { Text("Subscriptions for $expandedUser") }
                    userSubs.forEach { sub ->
                        Div({ style { display(DisplayStyle.Flex); gap(Spacing.md); padding(Spacing.xs, 0.px); color(Color(colors.text)); fontSize(Typography.sm) } }) {
                            Span({ style { fontWeight("600"); property("min-width", "80px") } }) { Text(sub.entityType) }
                            Span({ style { property("min-width", "150px") } }) { Text(sub.entityNameEn ?: sub.entityId) }
                            Span({ style { color(Color(colors.textMuted)) } }) { Text(sub.events.joinToString(", ")) }
                        }
                    }
                }
            }
        }
    }
}

// ═══════════════════════════════════════════════════════════
//  SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════

@Composable
private fun EntityTypeBadge(colors: AppColors, entityType: String) {
    // Only set legacy entities apart when the revamp ones are shown next to them
    val isLegacy = SHOW_REVAMP && isLegacyType(entityType)
    val bgColor = if (isLegacy) colors.warning else colors.background
    val textColor = if (isLegacy) "#1a1d21" else colors.textMuted
    Span({
        style {
            badgeStyle(bgColor, textColor)
            if (isLegacy) property("border", "1px solid ${colors.warning}")
        }
    }) {
        Text(if (isLegacy) "LEGACY" else entityType.removePrefix("legacy_").uppercase())
    }
}

@Composable
private fun SectionHeader(colors: AppColors, text: String) {
    Div({
        style {
            fontWeight("600"); color(Color(colors.text)); fontSize(Typography.base)
            marginBottom(Spacing.sm); marginTop(Spacing.md)
            paddingBottom(Spacing.xs)
            property("border-bottom", "1px solid ${colors.border}")
        }
    }) { Text(text) }
}

@Composable
private fun SummaryRow(colors: AppColors, label: String, value: String) {
    Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.xs) } }) {
        Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); property("min-width", "90px") } }) { Text(label) }
        Span({ style { fontSize(Typography.sm); color(Color(colors.text)); fontWeight("500") } }) { Text(value) }
    }
}

@Composable
private fun RadioOption(colors: AppColors, label: String, selected: Boolean, onClick: () -> Unit) {
    Div({
        style { color(Color(colors.text)); cursor("pointer"); display(DisplayStyle.Flex); property("align-items", "center"); gap(Spacing.xs) }
        onClick { onClick() }
    }) {
        Input(type = org.jetbrains.compose.web.attributes.InputType.Radio) {
            checked(selected)
            onChange { onClick() }
        }
        Text(" $label")
    }
}

@Composable
private fun FieldLabel(colors: AppColors, text: String) {
    Div({ style { fontWeight("600"); color(Color(colors.text)); marginBottom(Spacing.xs); fontSize(Typography.sm) } }) { Text(text) }
}

@Composable
private fun InputField(colors: AppColors, label: String, value: String, placeholder: String = "", onChange: (String) -> Unit) {
    Div({ style { marginBottom(Spacing.sm) } }) {
        FieldLabel(colors, label)
        Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
            value(value)
            if (placeholder.isNotBlank()) placeholder(placeholder)
            onInput { onChange(it.value) }
            style { formInputStyle(colors) }
        }
    }
}

@Composable
private fun TextAreaField(colors: AppColors, label: String, value: String, onChange: (String) -> Unit) {
    Div({ style { marginBottom(Spacing.sm) } }) {
        FieldLabel(colors, label)
        TextArea {
            value(value)
            onInput { onChange(it.value) }
            style {
                formInputStyle(colors)
                property("min-height", "60px"); property("resize", "vertical")
            }
        }
    }
}

private fun utcToEgypt(isoUtc: String): String {
    return try {
        val utcDate = kotlin.js.Date(isoUtc.replace(" ", "T").let { if (!it.endsWith("Z")) "${it}Z" else it })
        // Egypt is UTC+2 (EET); no DST since 2014
        val egyptMs = utcDate.getTime() + 2 * 60 * 60 * 1000
        val egyptDate = kotlin.js.Date(egyptMs)
        val y = egyptDate.getUTCFullYear()
        val m = (egyptDate.getUTCMonth() + 1).toString().padStart(2, '0')
        val d = egyptDate.getUTCDate().toString().padStart(2, '0')
        val h = egyptDate.getUTCHours().toString().padStart(2, '0')
        val min = egyptDate.getUTCMinutes().toString().padStart(2, '0')
        "$y-$m-$d $h:$min"
    } catch (_: Exception) { isoUtc }
}

private fun remainingTime(isoUtc: String): String {
    return try {
        val target = kotlin.js.Date(isoUtc.replace(" ", "T").let { if (!it.endsWith("Z")) "${it}Z" else it })
        val now = kotlin.js.Date()
        val diffMs = target.getTime() - now.getTime()
        if (diffMs < 0) {
            val ago = (-diffMs / 60000).toLong()
            if (ago < 60) "-${ago}m overdue" else "-${ago / 60}h ${ago % 60}m overdue"
        } else {
            val mins = (diffMs / 60000).toLong()
            if (mins < 60) "in ${mins}m"
            else if (mins < 1440) "in ${mins / 60}h ${mins % 60}m"
            else "in ${mins / 1440}d ${(mins % 1440) / 60}h"
        }
    } catch (_: Exception) { "" }
}
